import { useEffect, useMemo, useState } from 'react';
import { Button, Input, Select, useToast, Modal, Skeleton, EmptyState, Badge, DataTable, Column, LongText, NameCell } from '../../../shared/components';
import { getTurnoGeoRepository } from '../../../shared/services/turnoGeo/repositoryFactory';
import { getSettingsRepository } from '../../../shared/services/settings/repositoryFactory';
import { ApplicationMember } from '../../../shared/services/settings/types';
import { SaveScheduleExceptionInput, ScheduleException, TurnoGeoSettings } from '../../../shared/services/turnoGeo/types';
import { InfoTip } from './InfoTip';
import { DAY_NAMES, DAY_ORDER, describeDays, parseDays } from '../jornadaUtil';

const repo = getTurnoGeoRepository();
const settingsRepo = getSettingsRepository();

type Form = {
  userId: string;
  workDays: number[];
  startTime: string;
  endTime: string;
  lunchStartTime: string;
  lunchEndTime: string;
  lunchMinutes: string;
  validFrom: string;
  validTo: string;
  reason: string;
};

const today = () => new Date().toLocaleDateString('en-CA');

/**
 * Configuración > Excepciones de horario: empleados cuyo horario se sale del horario general
 * (que vive en Parámetros). Cada excepción tiene vigencia, días y motivo; la vigente
 * sobrescribe al horario general solo para esa persona y esos días.
 */
export function ScheduleExceptionsTab() {
  const toast = useToast();
  const [rows, setRows] = useState<ScheduleException[] | null>(null);
  const [members, setMembers] = useState<ApplicationMember[]>([]);
  const [settings, setSettings] = useState<TurnoGeoSettings | null>(null);
  const [editing, setEditing] = useState<ScheduleException | 'new' | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [toDelete, setToDelete] = useState<ScheduleException | null>(null);

  const load = () => {
    Promise.all([repo.listScheduleExceptions(), settingsRepo.listApplicationMembers('turno-geo', true), repo.getConfiguration()]).then(([ex, m, cfg]) => {
      setRows(ex);
      setMembers(m.filter((x) => x.hasAccess));
      setSettings(cfg);
    });
  };
  useEffect(load, []);

  const blankForm = (cfg: TurnoGeoSettings): Form => ({
    userId: '',
    workDays: parseDays(cfg.workDays),
    startTime: cfg.workStartTime,
    endTime: cfg.workEndTime,
    lunchStartTime: cfg.lunchStartTime,
    lunchEndTime: cfg.lunchEndTime,
    lunchMinutes: '',
    validFrom: today(),
    validTo: '',
    reason: '',
  });

  const openNew = () => {
    if (!settings) return;
    setForm(blankForm(settings));
    setEditing('new');
  };
  const openEdit = (e: ScheduleException) => {
    setForm({
      userId: e.userId,
      workDays: e.workDays,
      startTime: e.startTime,
      endTime: e.endTime,
      lunchStartTime: e.lunchStartTime,
      lunchEndTime: e.lunchEndTime,
      lunchMinutes: e.lunchMinutes === null ? '' : String(e.lunchMinutes),
      validFrom: e.validFrom,
      validTo: e.validTo ?? '',
      reason: e.reason,
    });
    setEditing(e);
  };

  const patch = (p: Partial<Form>) => setForm((f) => (f ? { ...f, ...p } : f));
  const toggleDay = (d: number) => form && patch({ workDays: form.workDays.includes(d) ? form.workDays.filter((x) => x !== d) : [...form.workDays, d] });

  const net = useMemo(() => {
    if (!form || !settings) return null;
    const hm = (v: string) => { const [h, m] = v.split(':').map(Number); return h * 60 + m; };
    if (![form.startTime, form.endTime].every((v) => /^\d{2}:\d{2}$/.test(v))) return null;
    const s = hm(form.startTime);
    const e = hm(form.endTime);
    const gross = e > s ? e - s : e + 1440 - s;
    const lunch = form.lunchMinutes === '' ? settings.lunchMinutes : Number(form.lunchMinutes);
    return { net: Math.max(0, gross - lunch), crosses: e <= s };
  }, [form, settings]);

  const save = async () => {
    if (!form || !editing) return;
    const input: SaveScheduleExceptionInput = {
      userId: form.userId,
      workDays: form.workDays,
      startTime: form.startTime,
      endTime: form.endTime,
      lunchStartTime: form.lunchStartTime,
      lunchEndTime: form.lunchEndTime,
      lunchMinutes: form.lunchMinutes === '' ? null : Number(form.lunchMinutes),
      validFrom: form.validFrom,
      validTo: form.validTo || null,
      reason: form.reason,
    };
    setIsSaving(true);
    try {
      if (editing === 'new') await repo.createScheduleException(input);
      else await repo.updateScheduleException(editing.id, input);
      toast.show('Excepción guardada.');
      setEditing(null);
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar la excepción.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await repo.deleteScheduleException(toDelete.id);
      toast.show('Excepción eliminada.');
      setToDelete(null);
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo eliminar.', 'error');
    }
  };

  if (rows === null) return <Skeleton className="h-72 rounded-2xl" />;

  const columns: Column<ScheduleException>[] = [
    {
      key: 'empleado',
      header: 'Empleado',
      sortable: true,
      sortValue: (r) => r.userFullName,
      render: (r) => <NameCell name={r.userFullName} />,
    },
    { key: 'dias', filterValue: (r) => describeDays(r.workDays), header: 'Días', render: (r) => describeDays(r.workDays) },
    {
      key: 'horario',
      header: 'Horario',
      render: (r) => (
        <span className="whitespace-nowrap">
          {r.startTime} – {r.endTime}
          <span className="block text-xs text-ink-400">Almuerzo {r.lunchStartTime} – {r.lunchEndTime}{r.lunchMinutes !== null ? ` (${r.lunchMinutes} min)` : ''}</span>
        </span>
      ),
    },
    {
      key: 'vigencia',
      header: 'Vigencia',
      sortable: true,
      sortValue: (r) => r.validFrom,
      render: (r) => (
        <span className="whitespace-nowrap">
          {r.validFrom} → {r.validTo ?? 'sin fin'}
          {r.validTo !== null && r.validTo < today() && <Badge tone="neutral">Vencida</Badge>}
        </span>
      ),
    },
    { key: 'motivo', header: 'Motivo', render: (r) => <span className="text-ink-600"><LongText text={r.reason} max={60} /></span> },
    {
      key: 'acciones',
      header: '',
      render: (r) => (
        <div className="flex gap-2">
          <Button variant="outline" size="md" onClick={() => openEdit(r)}>Editar</Button>
          <Button variant="ghost" size="md" onClick={() => setToDelete(r)}>Eliminar</Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-ink-100 bg-surface p-4">
        <p className="flex items-center gap-2 text-sm font-semibold text-ink-800">
          Horarios de excepción
          <InfoTip
            title="¿Qué es una excepción?"
            text="El horario general (entrada, salida, almuerzo y días laborados) se define en Parámetros y aplica a todos. Aquí registras a los empleados cuyo horario es distinto: solo para ellos, en los días y fechas indicados, se usa el horario de la excepción en lugar del general."
          />
        </p>
        {settings && (
          <p className="mt-1 text-xs text-ink-500">
            Horario general vigente: {settings.workStartTime} – {settings.workEndTime} · Almuerzo {settings.lunchStartTime} – {settings.lunchEndTime} · {describeDays(parseDays(settings.workDays))}
          </p>
        )}
        <Button className="mt-3" size="md" onClick={openNew} disabled={!settings}>Nueva excepción</Button>
      </div>

      {rows.length === 0 ? (
        <EmptyState icon="🕒" title="Sin excepciones" description="Todos los empleados usan el horario general de Parámetros." />
      ) : (
        <DataTable excel fixedKey="empleado" columns={columns} rows={rows} rowKey={(r) => r.id} />
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing === 'new' ? 'Nueva excepción de horario' : 'Editar excepción'}>
        {form && (
          <div className="space-y-3">
            <Select
              label="Empleado"
              value={form.userId}
              onChange={(e) => patch({ userId: e.target.value })}
              placeholder="Selecciona…"
              disabled={editing !== 'new'}
              options={members.map((m) => ({ value: m.id, label: m.fullName }))}
            />
            <div>
              <p className="mb-1.5 text-sm font-medium text-ink-700">Días laborados</p>
              <div className="flex flex-wrap gap-2">
                {DAY_ORDER.map((d) => (
                  <label key={d} className={['flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs', form.workDays.includes(d) ? 'border-brand-400 bg-brand-light text-brand-700' : 'border-ink-200 text-ink-600'].join(' ')}>
                    <input type="checkbox" className="h-3.5 w-3.5" checked={form.workDays.includes(d)} onChange={() => toggleDay(d)} />
                    {DAY_NAMES[d]}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Hora de entrada" type="time" value={form.startTime} onChange={(e) => patch({ startTime: e.target.value })} />
              <Input label="Hora de salida" type="time" value={form.endTime} onChange={(e) => patch({ endTime: e.target.value })} />
              <Input label="Sale a almorzar" type="time" value={form.lunchStartTime} onChange={(e) => patch({ lunchStartTime: e.target.value })} />
              <Input label="Regresa del almuerzo" type="time" value={form.lunchEndTime} onChange={(e) => patch({ lunchEndTime: e.target.value })} />
            </div>
            <Input
              label="Minutos de almuerzo a descontar (opcional)"
              type="number"
              min={0}
              max={240}
              value={form.lunchMinutes}
              onChange={(e) => patch({ lunchMinutes: e.target.value })}
              hint={`Vacío = usa el general (${settings?.lunchMinutes ?? 60} min).`}
            />
            {net && (
              <p className="rounded-xl bg-ink-50 p-3 text-xs text-ink-600">
                Jornada neta por día: <strong>{Math.floor(net.net / 60)} h {net.net % 60} min</strong>
                {net.crosses && ' · el turno cruza la medianoche'}
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Input label="Vigente desde" type="date" value={form.validFrom} onChange={(e) => patch({ validFrom: e.target.value })} />
              <Input label="Hasta (opcional)" type="date" value={form.validTo} onChange={(e) => patch({ validTo: e.target.value })} hint="Vacío = sin fecha final." />
            </div>
            <Input label="Motivo" value={form.reason} maxLength={300} onChange={(e) => patch({ reason: e.target.value })} placeholder="Ej.: turno de madrugada acordado con el líder" />
            <div className="flex gap-3 pt-2">
              <Button variant="outline" fullWidth onClick={() => setEditing(null)}>Cancelar</Button>
              <Button fullWidth onClick={save} isLoading={isSaving} disabled={!form.userId || form.workDays.length === 0 || !form.reason.trim()}>Guardar</Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!toDelete} onClose={() => setToDelete(null)} title="Eliminar excepción">
        <p className="text-sm text-ink-600">
          {toDelete ? `¿Eliminar la excepción de ${toDelete.userFullName}? Desde hoy volverá a regir el horario general para esa persona.` : ''}
        </p>
        <div className="mt-4 flex gap-3">
          <Button variant="outline" fullWidth onClick={() => setToDelete(null)}>Cancelar</Button>
          <Button variant="danger" fullWidth onClick={confirmDelete}>Eliminar</Button>
        </div>
      </Modal>
    </div>
  );
}
